import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Heading,
  useToast,
  ButtonGroup,
  Box,
  FormLabel,
  SimpleGrid,
  Flex,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import axios from 'axios';
import moment from 'moment';
import { isSameDay } from 'date-fns';
import { DatePicker } from '../../../../../../../../../../../../../components/Form/DatePicker';
import { MaskedInput } from '../../../../../../../../../../../../../components/Form/MaskedInput';
import { useActivity } from '../../../../../../../../../../../../../hooks/activity';
import { createActivitySchedulesService } from '../../../../../../../../../../../../../services/Activities/CreateActivitySchedulesService';
import { updateActivitySchedulesService } from '../../../../../../../../../../../../../services/Activities/UpdateActivitySchedulesService';
import { translateError } from '../../../../../../../../../../../../../utils/errors';
import {
  ReactSelect,
  SelectOption,
} from '../../../../../../../../../../../../../components/Form/ReactSelect';
import { Switch } from '../../../../../../../../../../../../../components/Form/Switch';
import {
  maskMoney,
  unmaskNumber,
} from '../../../../../../../../../../../../../utils/formatters/handleMask';
import { ConfirmationModal } from '../../../../../../../../../../../../../components/ConfirmationModal';
import { WaveSideEnum } from '../../../../../../../../../../../../../models/activities';
import { DatePickerMultiple } from '../../../../../../../../../../../../../components/Form/DatePickerMultiple';
import { NumberInput } from '../../../../../../../../../../../../../components/Form/NumberInput';
import { IActivityScheduleListItem } from '../../../../../../../../../../../../../services/Activities/ListActivitySchedulesService';
import { useAuth } from '../../../../../../../../../../../../../hooks/auth';

export interface IActivitySchedule extends IActivityScheduleListItem {
  formattedEndDate: string;
  formattedEndTime: string;
  formattedStartDate: string;
  formattedStartTime: string;
  formattedBookingPrice: string;
  formattedGuestAdditionalBookingPrice: string;
}

interface IHandleActivityScheduleModalFormData {
  allowLinkBookings?: boolean;
  allowPartialTimeBookings?: boolean;
  bookingPrice: number;
  guestAdditionalBookingPrice: number;
  endDate?: string;
  endTime: string;
  guestVacancies?: number;
  lodgerVacancies?: number;
  queueVacancies: number;
  isActive: boolean;
  minUserExperience: number;
  modalityId?: string | null;
  waveSide?: WaveSideEnum;
  startDate: string;
  startTime: string;
  totalVacancies?: number;
}

interface IHandleActivityScheduleModalProps {
  activitySchedule?: IActivitySchedule;
  isOpen: boolean;
  onClose: () => void;
  onSave: (schedule: IActivitySchedule, action: 'create' | 'update') => void;
}

const activityScheduleFormSchema = Yup.object().shape({
  allowLinkBookings: Yup.boolean().default(false),
  allowPartialTimeBookings: Yup.boolean().default(false),
  bookingPrice: Yup.number()
    .integer('Valores inteiros')
    .min(0, 'Valores positivos')
    .nullable()
    .transform((_, originalValue) =>
      originalValue === '' ? null : unmaskNumber(originalValue),
    )
    .required('Requerido'),
  guestAdditionalBookingPrice: Yup.number()
    .integer('Valores inteiros')
    .min(0, 'Valores positivos')
    .nullable()
    .transform((_, originalValue) =>
      originalValue === '' ? null : unmaskNumber(originalValue),
    )
    .required('Requerido'),
  endDate: Yup.date()
    .nullable()
    .min(Yup.ref('startDate'), 'Data inválida')
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  endTime: Yup.string()
    .matches(/^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/, 'Horário inválido')
    .required('Requerido')
    .nullable()
    .test(
      'is-greater',
      'Horário final deve ser maior que o inicial',
      function isSameOrAfter(value) {
        return moment(value, 'HH:mm').isSameOrAfter(
          moment(this.parent.startTime, 'HH:mm'),
        );
      },
    )
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  guestVacancies: Yup.number().when('totalVacancies', {
    is: (val?: number) => typeof val === 'number',
    then: Yup.number()
      .integer('Valores inteiros')
      .min(0, 'Valores positivos')
      .max(Yup.ref('totalVacancies'), 'Máximo igual ao total de vagas')
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' ? null : value,
      )
      .required('Requerido'),
    otherwise: Yup.number()
      .integer('Valores inteiros')
      .min(0, 'Valores positivos')
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' ? null : value,
      ),
  }),
  lodgerVacancies: Yup.number().when('totalVacancies', {
    is: (val?: number) => typeof val === 'number',
    then: Yup.number()
      .integer('Valores inteiros')
      .min(0, 'Valores positivos')
      .max(Yup.ref('totalVacancies'), 'Máximo igual ao total de vagas')
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' ? null : value,
      )
      .required('Requerido'),
    otherwise: Yup.number()
      .integer('Valores inteiros')
      .min(0, 'Valores positivos')
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' ? null : value,
      ),
  }),
  queueVacancies: Yup.number().when('totalVacancies', {
    is: (val?: number) => typeof val === 'number',
    then: Yup.number()
      .integer('Valores inteiros')
      .min(0, 'Valores positivos')
      .max(Yup.ref('totalVacancies'), 'Máximo igual ao total de vagas')
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' ? null : value,
      )
      .required('Requerido'),
    otherwise: Yup.number()
      .integer('Valores inteiros')
      .min(0, 'Valores positivos')
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' ? null : value,
      )
      .required('Requerido'),
  }),
  isActive: Yup.boolean().default(true),
  modalityId: Yup.string()
    .uuid()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  startDate: Yup.date()
    .required('Requerido')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  startTime: Yup.string()
    .matches(/^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/, 'Horário inválido')
    .required('Requerido')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  totalVacancies: Yup.number()
    .nullable()
    .integer('Valores inteiros')
    .positive('Valores maiores que zero')
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  waveSide: Yup.string().oneOf(['COMBO', 'LEFT', 'RIGHT', null]).nullable(),
});

export const HandleActivityScheduleModal = ({
  activitySchedule,
  isOpen,
  onClose,
  onSave,
}: IHandleActivityScheduleModalProps): JSX.Element => {
  const toast = useToast();
  const { activity } = useActivity();

  const { assignment: authenticatedAssignment, experienceLevelsTitleMap } =
    useAuth();

  const formRef = useRef<HTMLElement & HTMLFormElement>(null);
  const { register, formState, handleSubmit, reset, control, watch, setValue } =
    useForm({
      resolver: yupResolver(activityScheduleFormSchema),
    });

  const { errors } = formState;

  const selectedStartDate: Date | undefined = watch('startDate');
  const selectedEndDate: Date | undefined = watch('endDate');

  const currentAllowPartialTimeBookings = watch('allowPartialTimeBookings');

  const [scheduleBlocks, setScheduleBlocks] = useState<Date[]>([]);
  const [selectedWeekDays, setSelectedWeekDays] = useState([
    0, 1, 2, 3, 4, 5, 6,
  ]);
  const [
    isVerifyConfirmationModalVisible,
    setIsVerifyConfirmationModalVisible,
  ] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [modalitiesSelectOptions, setModalitiesSelectOptions] = useState<
    SelectOption[]
  >([]);

  const weekDays = useMemo(
    () => [
      {
        label: 'D',
        value: 0,
      },
      {
        label: 'S',
        value: 1,
      },
      {
        label: 'T',
        value: 2,
      },
      {
        label: 'Q',
        value: 3,
      },
      {
        label: 'Q',
        value: 4,
      },
      {
        label: 'S',
        value: 5,
      },
      {
        label: 'S',
        value: 6,
      },
    ],
    [],
  );

  const experienceSelectOptions = Array.from(
    experienceLevelsTitleMap,
    ([value, label]) => ({
      label,
      value,
    }),
  );

  const waveSideSelectOptions = useMemo(
    () => [
      {
        label: 'Indefinido',
        value: null,
      },
      {
        label: 'Combo',
        value: 'COMBO',
      },
      {
        label: 'Esquerda',
        value: 'LEFT',
      },
      {
        label: 'Direita',
        value: 'RIGHT',
      },
    ],
    [],
  );

  useEffect(() => {
    if (
      !authenticatedAssignment?.featureGroup.features.some(
        (feature) => feature.key === 'LODGER_VACANCIES_FULL_ACCESS',
      )
    ) {
      setValue('lodgerVacancies', '0');
    }
  }, [authenticatedAssignment?.featureGroup.features, setValue, isOpen]);

  useEffect(() => {
    if (activity) {
      setModalitiesSelectOptions([
        {
          label: 'Todas',
          value: '',
        },
        ...activity.spot.modalities
          .filter((modality) => modality.isActive)
          .map((modality) => ({
            label: modality.title,
            value: modality.id,
          })),
      ]);
    }
  }, [activity]);

  useEffect(() => {
    if (activitySchedule) {
      const [startDateYear, startDateMonth, startDateDay] =
        activitySchedule.startDate.split('-');
      const [endDateYear, endDateMonth, endDateDay] =
        activitySchedule.endDate?.split('-') || [];

      reset({
        allowLinkBookings: activitySchedule.allowLinkBookings,
        allowPartialTimeBookings: activitySchedule.allowPartialTimeBookings,
        bookingPrice: maskMoney(activitySchedule.bookingPrice),
        guestAdditionalBookingPrice: maskMoney(
          activitySchedule.guestAdditionalBookingPrice,
        ),
        endDate: activitySchedule.endDate
          ? new Date(
              Number(endDateYear),
              Number(endDateMonth) - 1,
              Number(endDateDay),
            )
          : undefined,
        endTime: activitySchedule.endTime.slice(0, 5),
        isActive: activitySchedule.isActive,
        minUserExperience: activitySchedule.minUserExperience,
        modalityId: activitySchedule.modalityId || '',
        startDate: new Date(
          Number(startDateYear),
          Number(startDateMonth) - 1,
          Number(startDateDay),
        ),
        startTime: activitySchedule.startTime.slice(0, 5),
        guestVacancies: activitySchedule.guestVacancies,
        lodgerVacancies: activitySchedule.lodgerVacancies,
        queueVacancies: activitySchedule.queueVacancies,
        totalVacancies: activitySchedule.totalVacancies,
        waveSide: activitySchedule.waveSide || null,
      });

      setScheduleBlocks(
        activitySchedule.scheduleBlocks.map(
          (scheduleBlock) => new Date(`${scheduleBlock.blockedDate}T00:00:00`),
        ),
      );

      setSelectedWeekDays(activitySchedule.daysOfWeek);
    } else {
      reset({
        allowLinkBookings: false,
        allowPartialTimeBookings: false,
        endDate: null,
        endTime: null,
        isActive: true,
        minUserExperience: 0,
        modalityId: '',
        startDate: null,
        startTime: null,
        guestVacancies: null,
        lodgerVacancies: null,
        queueVacancies: null,
        totalVacancies: null,
        waveSide: null,
      });

      setScheduleBlocks([]);
      setSelectedWeekDays([0, 1, 2, 3, 4, 5, 6]);
    }
  }, [activitySchedule, reset]);

  useEffect(() => {
    setValue('guestVacancies', null);
    setValue('lodgerVacancies', null);
    setValue('queueVacancies', null);
    setValue('totalVacancies', null);
  }, [currentAllowPartialTimeBookings, setValue]);

  const handleTogleSelectWeekDays = useCallback((weekDayValue: number) => {
    setSelectedWeekDays((prevState) =>
      prevState.includes(weekDayValue)
        ? prevState.filter((selectedDay) => selectedDay !== weekDayValue)
        : [...prevState, weekDayValue],
    );
  }, []);

  const handleToggleVerifyConfirmationModal = useCallback(() => {
    setIsVerifyConfirmationModalVisible((prevState) => !prevState);
  }, []);

  const handleScheduleBlocks = (value: Date): void => {
    setScheduleBlocks((prevState) => {
      const blockExists = prevState.find((scheduleBlock) =>
        isSameDay(scheduleBlock, value),
      );

      if (blockExists) {
        return prevState.filter(
          (scheduleBlock) => scheduleBlock !== blockExists,
        );
      }

      return [...prevState, value];
    });
  };

  const handleActivityScheduleSubmit: SubmitHandler<IHandleActivityScheduleModalFormData> =
    useCallback(
      async ({
        allowLinkBookings,
        allowPartialTimeBookings,
        bookingPrice,
        guestAdditionalBookingPrice,
        endDate,
        endTime,
        guestVacancies,
        lodgerVacancies,
        queueVacancies,
        isActive,
        minUserExperience,
        modalityId,
        startDate,
        startTime,
        totalVacancies,
        waveSide,
      }) => {
        if (activity) {
          const parsedBlocks = scheduleBlocks.map((scheduleBlock) => ({
            blockedDate: scheduleBlock.toLocaleDateString('fr-CA'),
          }));

          if (!activitySchedule) {
            try {
              const newActivitySchedule = await createActivitySchedulesService({
                allowLinkBookings,
                allowPartialTimeBookings,
                activityId: activity.id,
                bookingPrice,
                guestAdditionalBookingPrice,
                daysOfWeek: selectedWeekDays,
                endDate: endDate
                  ? new Date(endDate).toISOString().split('T')[0]
                  : undefined,
                endTime: endTime.padEnd(8, ':00'),
                guestVacancies,
                lodgerVacancies,
                queueVacancies,
                isActive,
                minUserExperience,
                modalityId,
                scheduleBlocks: parsedBlocks,
                startDate: new Date(startDate).toISOString().split('T')[0],
                startTime: startTime.padEnd(8, ':00'),
                totalVacancies,
                waveSide,
              });

              const [startDateYear, startDateMonth, startDateDay] =
                newActivitySchedule.startDate.split('-');
              const [endDateYear, endDateMonth, endDateDay] =
                newActivitySchedule.endDate?.split('-') || [];

              onSave(
                {
                  ...newActivitySchedule,
                  formattedEndDate: newActivitySchedule.endDate
                    ? `${endDateDay}/${endDateMonth}/${endDateYear}`
                    : '-',
                  formattedEndTime: newActivitySchedule.endTime.slice(0, 5),
                  formattedStartDate: `${startDateDay}/${startDateMonth}/${startDateYear}`,
                  formattedStartTime: newActivitySchedule.startTime.slice(0, 5),
                  formattedBookingPrice: maskMoney(
                    newActivitySchedule.bookingPrice,
                    { hideSymbol: true },
                  ),
                  formattedGuestAdditionalBookingPrice: maskMoney(
                    newActivitySchedule.guestAdditionalBookingPrice,
                    { hideSymbol: true },
                  ),
                },
                'create',
              );

              reset({
                allowLinkBookings: false,
                allowPartialTimeBookings: false,
                bookingPrice: null,
                guestAdditionalBookingPrice: null,
                endDate: null,
                endTime: null,
                isActive: true,
                minUserExperience: 0,
                modalityId: '',
                startDate: null,
                startTime: null,
                guestVacancies: null,
                lodgerVacancies: null,
                queueVacancies: null,
                totalVacancies: null,
                waveSide: null,
              });

              setScheduleBlocks([]);
              setSelectedWeekDays([0, 1, 2, 3, 4, 5, 6]);

              toast({
                title: 'Cadastrado com sucesso',
                description: 'Evento cadastrado corretamente.',
                status: 'success',
                duration: 3000,
                isClosable: true,
                variant: 'subtle',
                position: 'top-right',
              });
            } catch (err) {
              if (axios.isAxiosError(err) && err.response?.status !== 401) {
                toast({
                  title: 'Falha ao cadastrar',
                  description:
                    translateError({ message: err.response?.data.message }) ||
                    'Ocorreu um erro ao cadastrar o evento, tente novamente.',
                  status: 'error',
                  duration: 3000,
                  isClosable: true,
                  variant: 'subtle',
                  position: 'top-right',
                });
              }
            }
          } else {
            try {
              const updatedActivitySchedule =
                await updateActivitySchedulesService({
                  activityScheduleId: activitySchedule.id,
                  allowLinkBookings,
                  allowPartialTimeBookings,
                  bookingPrice,
                  guestAdditionalBookingPrice,
                  daysOfWeek: selectedWeekDays,
                  endDate: endDate
                    ? new Date(endDate).toISOString().split('T')[0]
                    : null,
                  endTime: endTime.padEnd(8, ':00'),
                  guestVacancies: guestVacancies ?? null,
                  lodgerVacancies: lodgerVacancies ?? null,
                  queueVacancies,
                  isActive,
                  isVerified,
                  minUserExperience,
                  modalityId,
                  scheduleBlocks: parsedBlocks,
                  startDate: new Date(startDate).toISOString().split('T')[0],
                  startTime: startTime.padEnd(8, ':00'),
                  totalVacancies: totalVacancies ?? null,
                  waveSide,
                });

              const [startDateYear, startDateMonth, startDateDay] =
                updatedActivitySchedule.startDate.split('-');
              const [endDateYear, endDateMonth, endDateDay] =
                updatedActivitySchedule.endDate?.split('-') || [];

              onSave(
                {
                  ...updatedActivitySchedule,
                  formattedEndDate: updatedActivitySchedule.endDate
                    ? `${endDateDay}/${endDateMonth}/${endDateYear}`
                    : '-',
                  formattedEndTime: updatedActivitySchedule.endTime.slice(0, 5),
                  formattedStartDate: `${startDateDay}/${startDateMonth}/${startDateYear}`,
                  formattedStartTime: updatedActivitySchedule.startTime.slice(
                    0,
                    5,
                  ),
                  formattedBookingPrice: maskMoney(
                    updatedActivitySchedule.bookingPrice,
                    { hideSymbol: true },
                  ),
                  formattedGuestAdditionalBookingPrice: maskMoney(
                    updatedActivitySchedule.guestAdditionalBookingPrice,
                    { hideSymbol: true },
                  ),
                },
                'update',
              );

              reset({
                allowLinkBookings: false,
                allowPartialTimeBookings: false,
                bookingPrice: null,
                guestAdditionalBookingPrice: null,
                endDate: null,
                endTime: null,
                isActive: true,
                minUserExperience: 0,
                modalityId: '',
                startDate: null,
                startTime: null,
                guestVacancies: null,
                lodgerVacancies: null,
                queueVacancies: null,
                totalVacancies: null,
                waveSide: null,
              });

              setScheduleBlocks([]);
              setSelectedWeekDays([0, 1, 2, 3, 4, 5, 6]);
              setIsVerified(false);
              setIsVerifyConfirmationModalVisible(false);

              toast({
                title: 'Atualizado com sucesso',
                description: 'Evento atualizado corretamente.',
                status: 'success',
                duration: 3000,
                isClosable: true,
                variant: 'subtle',
                position: 'top-right',
              });
            } catch (err) {
              if (axios.isAxiosError(err) && err.response?.status !== 401) {
                if (err.response?.data.message === 'has-bookings-pending') {
                  setIsVerifyConfirmationModalVisible(true);
                  return;
                }

                toast({
                  title: 'Falha ao atualizar',
                  description:
                    translateError({ message: err.response?.data.message }) ||
                    'Ocorreu um erro ao atualizar o evento, tente novamente.',
                  status: 'error',
                  duration: 3000,
                  isClosable: true,
                  variant: 'subtle',
                  position: 'top-right',
                });
              }
            }
          }
        }
      },
      [
        activity,
        activitySchedule,
        isVerified,
        onSave,
        reset,
        scheduleBlocks,
        selectedWeekDays,
        toast,
      ],
    );

  const handleVerify = useCallback(() => {
    setIsVerified(true);
  }, []);

  useEffect(() => {
    if (isVerified) {
      formRef.current?.requestSubmit();
    }
  }, [isVerified]);

  const handleCloseModal = useCallback(() => {
    reset({
      allowLinkBookings: false,
      allowPartialTimeBookings: false,
      bookingPrice: null,
      guestAdditionalBookingPrice: null,
      endDate: null,
      endTime: null,
      isActive: true,
      minUserExperience: 0,
      modalityId: '',
      startDate: null,
      startTime: null,
      guestVacancies: null,
      lodgerVacancies: null,
      queueVacancies: null,
      totalVacancies: null,
      waveSide: null,
    });

    setScheduleBlocks([]);
    setSelectedWeekDays([0, 1, 2, 3, 4, 5, 6]);

    onClose();
  }, [onClose, reset]);

  return (
    <Modal size="2xl" isOpen={isOpen} onClose={handleCloseModal}>
      <ConfirmationModal
        isOpen={isVerifyConfirmationModalVisible}
        onClose={handleToggleVerifyConfirmationModal}
        onConfirm={handleVerify}
        message="Existem reservas pendentes, confirmar ação?"
      />

      <ModalOverlay />

      <ModalContent
        ref={formRef}
        as="form"
        onSubmit={handleSubmit(handleActivityScheduleSubmit)}
      >
        <ModalHeader>
          <Heading size="lg" fontWeight="normal">
            Evento
          </Heading>
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <SimpleGrid minChildWidth="240px" spacing="8" w="100%">
            <DatePicker
              label="Data início"
              isClearable={false}
              minDate={new Date()}
              control={control}
              error={errors.startDate}
              {...register('startDate')}
            />

            <DatePicker
              label="Data fim"
              control={control}
              minDate={selectedStartDate || new Date()}
              error={errors.endDate}
              {...register('endDate')}
            />

            <MaskedInput
              label="Horário início"
              mask="time"
              error={errors.startTime}
              {...register('startTime')}
            />

            <MaskedInput
              label="Horário fim"
              mask="time"
              error={errors.endTime}
              {...register('endTime')}
            />
          </SimpleGrid>

          {!currentAllowPartialTimeBookings &&
            authenticatedAssignment?.featureGroup.features.some(
              (feature) => feature.key === 'LODGER_VACANCIES_FULL_ACCESS',
            ) && (
              <Grid
                templateColumns={['repeat(12, 1fr)']}
                gap="4"
                mt="8"
                width="100%"
              >
                <GridItem colSpan={[12, 4, 3]}>
                  <NumberInput
                    label="Vagas total"
                    error={errors.totalVacancies}
                    {...register('totalVacancies')}
                  />
                </GridItem>

                <GridItem colSpan={[12, 4, 3]}>
                  <NumberInput
                    label="Vagas fila"
                    error={errors.queueVacancies}
                    {...register('queueVacancies')}
                  />
                </GridItem>

                <GridItem colSpan={[12, 6, 3]}>
                  <NumberInput
                    label="Vagas convidados"
                    error={errors.guestVacancies}
                    {...register('guestVacancies')}
                  />
                </GridItem>

                <GridItem colSpan={[12, 6, 3]}>
                  <NumberInput
                    label="Vagas hóspedes"
                    error={errors.lodgerVacancies}
                    {...register('lodgerVacancies')}
                  />
                </GridItem>
              </Grid>
            )}

          {!currentAllowPartialTimeBookings &&
            !authenticatedAssignment?.featureGroup.features.some(
              (feature) => feature.key === 'LODGER_VACANCIES_FULL_ACCESS',
            ) && (
              <SimpleGrid minChildWidth="240px" spacing="8" w="100%" mt="8">
                <NumberInput
                  label="Vagas total"
                  error={errors.totalVacancies}
                  {...register('totalVacancies')}
                />

                <NumberInput
                  label="Vagas convidados"
                  error={errors.guestVacancies}
                  {...register('guestVacancies')}
                />
              </SimpleGrid>
            )}

          <SimpleGrid minChildWidth="240px" spacing="8" w="100%" mt="8">
            <MaskedInput
              label={
                !currentAllowPartialTimeBookings
                  ? 'Valor da reserva'
                  : 'Valor da reserva (por hora)'
              }
              mask="money"
              error={errors.bookingPrice}
              {...register('bookingPrice')}
            />

            {!currentAllowPartialTimeBookings && (
              <MaskedInput
                label="Valor convidados ou hóspedes"
                mask="money"
                error={errors.guestAdditionalBookingPrice}
                {...register('guestAdditionalBookingPrice')}
              />
            )}

            <ReactSelect
              label="Modalidade"
              name="modalityId"
              options={modalitiesSelectOptions}
              control={control}
              error={errors.modalityId}
            />

            {!activity?.spot.isLivingArea && (
              <>
                <ReactSelect
                  label="Lado da onda"
                  name="waveSide"
                  options={waveSideSelectOptions}
                  control={control}
                  error={errors.waveSide}
                />

                <ReactSelect
                  label="Nível de experiência mínimo"
                  name="minUserExperience"
                  options={experienceSelectOptions}
                  control={control}
                  error={errors.minUserExperience}
                />
              </>
            )}

            <DatePickerMultiple
              label="Datas bloqueadas"
              name="scheduleBlocks"
              maxDate={selectedEndDate}
              minDate={selectedStartDate || new Date()}
              onChange={handleScheduleBlocks}
              selectedDates={scheduleBlocks}
            />

            <Box>
              <FormLabel>Dias da semana</FormLabel>
              <ButtonGroup>
                {weekDays.map((weekDay) => (
                  <Button
                    key={String(weekDay.value)}
                    size="sm"
                    colorScheme={
                      selectedWeekDays.includes(weekDay.value) ? 'blue' : 'gray'
                    }
                    onClick={() => handleTogleSelectWeekDays(weekDay.value)}
                  >
                    {weekDay.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Box>

            <Flex mt="4" alignItems="flex-end" justifyContent="center">
              <Switch
                label="Ativo"
                error={errors.isActive}
                {...register('isActive')}
              />

              <Switch
                label="Reservas parciais"
                error={errors.allowPartialTimeBookings}
                {...register('allowPartialTimeBookings')}
              />

              <Switch
                label="Reservas pelo link"
                error={errors.allowLinkBookings}
                {...register('allowLinkBookings')}
              />
            </Flex>
          </SimpleGrid>
        </ModalBody>

        <ModalFooter mt={currentAllowPartialTimeBookings ? '8' : 0}>
          <ButtonGroup>
            <Button colorScheme="blackAlpha" onClick={handleCloseModal}>
              Cancelar
            </Button>

            <Button
              colorScheme="green"
              type="submit"
              isLoading={formState.isSubmitting}
            >
              Salvar
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
